import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Mail, Phone, Calendar, MapPin, FileText, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';
import { format } from 'date-fns';

const PersonnelDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: member, loading, error } = useApi(
    () => apiService.getCrewMember(Number(id)),
    [id]
  );

  const { data: qualifications } = useApi(
    () => apiService.getQualifications(Number(id)),
    [id]
  );

  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${member?.first_name} ${member?.last_name}? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await apiService.deleteCrewMember(Number(id));
      navigate('/personnel');
    } catch (error: any) {
      setDeleteError(error.message || 'Failed to delete personnel');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="glass-card p-6 text-center text-red-400">
        {error?.message || 'Personnel not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/personnel')}
            className="btn-secondary flex items-center gap-2 p-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {member.first_name} {member.last_name}
            </h1>
            <p className="mt-1 text-sm text-gray-400">Employee ID: {member.employee_id}</p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => alert('Edit functionality coming soon')}
            className="btn-secondary flex items-center gap-2"
          >
            <Edit className="h-5 w-5" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="btn-secondary flex items-center gap-2 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
          >
            <Trash2 className="h-5 w-5" />
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      {/* Delete Error */}
      {deleteError && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-400 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{deleteError}</span>
        </div>
      )}

      {/* Main Info */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Personal Information */}
        <div className="lg:col-span-2 space-y-6">
          <div className="glass-card p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">Personal Information</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm text-gray-400">First Name</label>
                <p className="text-white">{member.first_name}</p>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Last Name</label>
                <p className="text-white">{member.last_name}</p>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Email</label>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <p className="text-white">{member.email}</p>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Phone</label>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <p className="text-white">{member.phone}</p>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Date of Birth</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <p className="text-white">{format(new Date(member.date_of_birth), 'MMM dd, yyyy')}</p>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Hire Date</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <p className="text-white">{format(new Date(member.hire_date), 'MMM dd, yyyy')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Employment Information */}
          <div className="glass-card p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">Employment Information</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm text-gray-400">Position</label>
                <p className="text-white">{member.position}</p>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Rank</label>
                <span className="badge-sky">{member.rank}</span>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Base</label>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <p className="text-white">{member.base}</p>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Status</label>
                <span className={`badge ${member.status === 'active' ? 'badge-active' : 'badge-inactive'}`}>
                  {member.status}
                </span>
              </div>
            </div>
          </div>

          {/* Qualifications */}
          <div className="glass-card p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">Qualifications</h2>
            <div className="space-y-4">
              {qualifications?.map((qual) => (
                <div key={qual.id} className="rounded-lg border border-gray-800 bg-gray-900/30 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-white">{qual.qualification_type}</h3>
                      {qual.aircraft_type && (
                        <p className="text-sm text-gray-400">Aircraft: {qual.aircraft_type}</p>
                      )}
                      <p className="mt-2 text-sm text-gray-400">
                        Valid until: {format(new Date(qual.expiry_date), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <span className={`badge ${qual.status === 'valid' ? 'badge-active' : 'badge-expired'}`}>
                      {qual.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* License Information */}
          <div className="glass-card p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">License</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm text-gray-400">License Number</label>
                <p className="font-mono text-white">{member.license_number || 'N/A'}</p>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Expiry Date</label>
                <p className="text-white">
                  {member.license_expiry
                    ? format(new Date(member.license_expiry), 'MMM dd, yyyy')
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div className="glass-card p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">Medical</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm text-gray-400">Medical Class</label>
                <p className="text-white">{member.medical_class || 'N/A'}</p>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Expiry Date</label>
                <p className="text-white">
                  {member.medical_expiry
                    ? format(new Date(member.medical_expiry), 'MMM dd, yyyy')
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="glass-card p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">Emergency Contact</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm text-gray-400">Name</label>
                <p className="text-white">{member.emergency_contact_name || 'N/A'}</p>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-400">Phone</label>
                <p className="text-white">{member.emergency_contact_phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {member.notes && (
            <div className="glass-card p-6">
              <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold text-white">
                <FileText className="h-5 w-5" />
                Notes
              </h2>
              <p className="text-sm text-gray-300">{member.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonnelDetail;
